import styled, { css } from 'styled-components';
import { FormikProps, FormikValues } from 'formik';

import Footer from 'layout/footer/components/Footer';
import { GiftCardsFooterLinksDesktop } from 'gift-cards/components/GiftCardsFooterLinksDesktop';
import { GiftCardsFooterLinksMobile } from 'gift-cards/components/GiftCardsFooterLinksMobile';
import { GiftCardsActions } from 'gift-cards/state/giftCardsReducer';
import { useGiftCardsDispatch, useGiftCardsState } from 'gift-cards/hooks/useGiftCards';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { Button } from 'shared/components/Button';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'giftCards.components.GiftCardsFooter';

interface IGiftCardsFooterProps {
  detailsFormProps: FormikProps<FormikValues>;
  isFooterExpanded?: boolean;
  setIsFooterExpanded?: (val: boolean) => void;
}

export const GiftCardsFooter = ({
  detailsFormProps,
  isFooterExpanded,
  setIsFooterExpanded,
}: IGiftCardsFooterProps) => {
  const { isMobile } = useWindowSize();
  const dispatch = useGiftCardsDispatch();
  const { giftCardSelection, selectedSpaceId, spaceInMobileEditMode, spaces } = useGiftCardsState();

  if (isMobile && !giftCardSelection) {
    return null;
  }

  const handleContinue = () => {
    dispatch({ type: GiftCardsActions.setCurrentSpace, payload: selectedSpaceId });

    sendTrackingEvent('GC_CREATION_CONTINUE_TAP', {
      gift_card_space_id: selectedSpaceId,
      has_item: spaces[selectedSpaceId]?.items?.length > 0,
    });
  };

  const handleContinueToCheckout = () => {
    if (detailsFormProps) {
      detailsFormProps.handleSubmit();
    }
  };

  const handleDone = () => {
    dispatch({ type: GiftCardsActions.setSpaceInMobileEditMode, payload: { spaceId: null } });
  };

  return (
    <GiftCardsFooter.Footer $isFooterExpanded={isFooterExpanded}>
      <GiftCardsFooter.FooterTop $spaceInMobileEditMode={spaceInMobileEditMode}>
        {!spaceInMobileEditMode && (
          <GiftCardsFooter.GiftCard>{t(`${TP}.giftCard`, 'Gift Card')}</GiftCardsFooter.GiftCard>
        )}
        {giftCardSelection
          ? !spaceInMobileEditMode && (
              <GiftCardsFooter.Button
                $giftCardSelection={giftCardSelection}
                data-qa="gift-cards-pdp-continue"
                title={t(`${TP}.continue`, 'Continue')}
                inverted
                type="button"
                onClick={handleContinue}
              />
            )
          : !spaceInMobileEditMode && (
              <GiftCardsFooter.Button
                $giftCardSelection={giftCardSelection}
                $isValid={detailsFormProps.isValid}
                data-qa="gift-cards-pdp-continue-to-checkout"
                title={t(`${TP}.continueToCheckout`, 'Continue to Checkout')}
                inverted={!(detailsFormProps.isValid && detailsFormProps.dirty)}
                disabled={!detailsFormProps.dirty || detailsFormProps.isSubmitting}
                loading={detailsFormProps.isSubmitting}
                type="submit"
                onClick={handleContinueToCheckout}
              />
            )}
        {giftCardSelection && spaceInMobileEditMode && (
          <GiftCardsFooter.Button
            $giftCardSelection={giftCardSelection}
            $spaceInMobileEditMode={spaceInMobileEditMode}
            data-qa="gift-cards-pdp-done"
            title={t(`${TP}.done`, 'Done')}
            inverted
            type="button"
            onClick={handleDone}
          />
        )}
        {isMobile ? (
          !spaceInMobileEditMode && <GiftCardsFooterLinksMobile />
        ) : (
          <GiftCardsFooterLinksDesktop
            isFooterExpanded={isFooterExpanded}
            setIsFooterExpanded={setIsFooterExpanded}
          />
        )}
      </GiftCardsFooter.FooterTop>
      {!isMobile && (
        <GiftCardsFooter.FooterBottom>
          <Footer />
        </GiftCardsFooter.FooterBottom>
      )}
    </GiftCardsFooter.Footer>
  );
};

GiftCardsFooter.Footer = styled.footer<{ $isFooterExpanded: boolean }>`
  width: 100%;
  bottom: 0;
  background-color: #fff;
  z-index: ${({ theme }) => theme.zIndex.pdpFooter};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    position: fixed;
    height: 198px;
    transition: transform 0.3s ease-in-out;
    transform: translateY(118px);
    pointer-events: auto;

    ${({ $isFooterExpanded }) =>
      $isFooterExpanded &&
      css`
        transform: translateY(0);
      `}
  }
`;

GiftCardsFooter.FooterTop = styled.div<{ $spaceInMobileEditMode?: string }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: ${({ $spaceInMobileEditMode }) => $spaceInMobileEditMode && `center`};
  align-items: ${({ $spaceInMobileEditMode }) => !$spaceInMobileEditMode && `flex-start`};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 75px;
  }
`;

GiftCardsFooter.Button = styled(Button)<{
  $giftCardSelection?: boolean;
  $spaceInMobileEditMode?: string;
  $isValid?: boolean;
}>`
  padding: 4px;
  height: 20px;
  justify-content: center;
  width: auto;
  flex-grow: 0 !important;
  text-transform: none;

  > span {
    ${variableFontRegular({ size: '11.5px', height: '12px' })};

    @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      ${variableFontRegular({ size: '11px', height: '12px' })};
    }
  }

  ${({ $giftCardSelection, theme, $isValid }) => {
    if ($giftCardSelection) {
      return css`
        height: 20px;
        text-transform: capitalize;
        font-size: 11.5px;

        &:hover {
          background-color: ${theme.colors.black};
          color: ${theme.colors.white};
        }
        &:active {
          width: 57px;
        }
      `;
    }
    if (!$isValid) {
      return css`
        cursor: default !important;
        min-width: 123.5px;
      `;
    }
    return css`
      min-width: 123.5px;
    `;
  }}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 32px;
    margin-top: 13px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: ${({ $spaceInMobileEditMode }) => !$spaceInMobileEditMode && '16px'};
  }

  ${({ $spaceInMobileEditMode }) =>
    $spaceInMobileEditMode &&
    css`
      padding: 4px;

      &:active {
        width: 35px;
      }
    `}
`;

GiftCardsFooter.FooterBottom = styled.div`
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  > footer {
    margin: 0;
    width: 100vw;
    padding: 8px 32px 24px;
    position: absolute;
  }
  > footer > div {
    min-height: unset;
  }
`;

GiftCardsFooter.GiftCard = styled.div`
  padding-left: 16px;
  justify-content: flex-start;
  flex: 1;
  margin-top: 1px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-top: 15px;
    padding-left: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;
